import { Link } from 'react-router-dom'
import comparePages from '@/data/comparePages.json'
import { Header } from '@/components/landing/Header'
import { Footer } from '@/components/landing/Footer'
import { Helmet } from 'react-helmet-async'
import { Breadcrumb } from '@/components/Breadcrumb'
import { motion } from 'framer-motion'
import { toAbsoluteUrl } from '@/lib/seo'

type ComparePageEntry = {
  slug: string
  title: string
  shortTitle: string
  excerpt: string
  featured?: boolean
  editionLabel?: string
}

const ComparePage = () => {
  const year = new Date().getFullYear()
  const compareGuides = Object.values(comparePages) as ComparePageEntry[]

  const breadcrumbs = [
    { name: 'Home', url: '/' },
    { name: 'Compare', url: '/compare' },
  ]

  const breadcrumbSchemaData = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbs.map((crumb, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: crumb.name,
      item: toAbsoluteUrl(crumb.url),
    })),
  }

  const featured = compareGuides.find((g) => g.featured)
  const allGuides = compareGuides

  return (
    <div className="min-h-screen w-full mx-auto relative overflow-hidden bg-gradient-to-b from-gray-50 to-white">
      <Helmet>
        <title>{`Customer Support Software Comparisons ${year} | Feedbask`}</title>
        <meta
          name="description"
          content="Compare Feedbask with other customer support and feedback tools. Feature comparison, pricing breakdown, and buying guides to choose the right solution."
        />
        <link rel="canonical" href={toAbsoluteUrl('/compare')} />
        <meta property="og:title" content={`Customer Support Software Comparisons ${year} | Feedbask`} />
        <meta property="og:description" content="Compare Feedbask with other customer support and feedback tools. Feature comparison and buying guides." />
        <meta property="og:url" content={toAbsoluteUrl('/compare')} />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchemaData)}</script>
      </Helmet>

      <div className="absolute top-0 left-0 w-full h-full pointer-events-none -z-10">
        <div className="absolute top-24 left-10 w-64 h-64 rounded-full bg-[#866BFF] opacity-30 blur-3xl" />
        <div className="absolute bottom-32 right-10 w-72 h-72 rounded-full bg-indigo-100 opacity-30 blur-3xl" />
      </div>

      <div className="relative z-10 max-w-7xl px-4 mx-auto">
        <Header />

        <Breadcrumb items={breadcrumbs} />

        <motion.section
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
          className="py-16 md:py-24"
        >
          <div className="text-center  mx-auto mb-16">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              Customer Support Software Comparisons
            </h1>
            <p className="text-xl text-gray-600">
              See how Feedbask stacks up against other customer support and feedback collection tools.
            </p>
          
          </div>

          {featured && (
            <div className="mb-16">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Featured Comparison Guide</h2>
              <p className="text-gray-600 mb-6">In-depth analysis of the best customer support software solutions.</p>
              <Link
                to={`/compare/${featured.slug}`}
                className="block bg-white rounded-xl shadow-md border border-gray-200 p-8 hover:shadow-lg transition group"
              >
                <span className="text-sm font-medium text-[#866BFF] uppercase tracking-wide">
                  Featured Guide {featured.editionLabel ? `· ${featured.editionLabel}` : ''}
                </span>
                <h3 className="text-2xl font-bold text-gray-900 mt-2 mb-3 group-hover:text-[#866BFF] transition">
                  {featured.title}
                </h3>
                <p className="text-gray-600 mb-4">{featured.excerpt}</p>
                <ul className="text-sm text-gray-500 space-y-1 mb-6">
                  <li>Complete feature comparison table</li>
                  <li>Detailed pros and cons</li>
                  <li>Expert recommendations by use case</li>
                </ul>
                <span className="text-[#866BFF] font-medium group-hover:underline">Read full comparison</span>
              </Link>
            </div>
          )}

          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">All Comparison Guides</h2>
            <p className="text-gray-600 mb-8">Comprehensive roundups to help you find the right tool.</p>
            <div className="grid md:grid-cols-2 gap-6">
              {allGuides.map((guide) => (
                <Link
                  key={guide.slug}
                  to={`/compare/${guide.slug}`}
                  className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition group"
                >
                  <h3 className="text-xl font-semibold text-gray-900 group-hover:text-[#866BFF] transition">
                    {guide.shortTitle}
                  </h3>
                  <p className="text-gray-600 mt-2 mb-4 line-clamp-2">{guide.excerpt}</p>
                  <span className="text-[#866BFF] font-medium text-sm group-hover:underline">Read full guide</span>
                </Link>
              ))}
            </div>
          </div>

          <div className="bg-[#866BFF] text-white rounded-2xl shadow-lg mt-20 p-10 text-center">
            <h2 className="text-3xl font-bold mb-4">Ready to collect better feedback?</h2>
            <p className="text-lg mb-6 max-w-2xl mx-auto">
              Feedbask helps you collect user feedback, bug reports, feature requests, and reviews with a simple widget. Set up in minutes.
            </p>
            <Link
              to="/auth"
              className="inline-block bg-white text-[#866BFF] font-semibold px-8 py-3 rounded-full hover:bg-gray-100 transition"
            >
              Get started for free
            </Link>
          </div>
        </motion.section>

        <Footer />
      </div>
    </div>
  )
}

export default ComparePage
